// src/lib/constants/app.ts

// ── App State Type IDs (as const, NOT TS enum) ──
export const APP_STATE_TYPE_ID = {
  Active: 1,
  Inactive: 2,
  Deleted: 3,
} as const;

// ── App Web Shared Domain IDs (as const, NOT TS enum) ──
export const APP_WEB_SHARED_DOMAIN_ID = {
  VideositeDotApp: 1, // videosite.app (direct customers)
  CloudsiteDotApp: 2, // cloudsite.app (resellers)
  SiteCDNDotApp: 3, // sitecdn.app (reserved)
} as const;

// ── App State Labels ──
export const APP_STATE_LABELS: Record<number, string> = {
  [APP_STATE_TYPE_ID.Active]: 'Active',
  [APP_STATE_TYPE_ID.Inactive]: 'Inactive',
  [APP_STATE_TYPE_ID.Deleted]: 'Deleted',
};

// ── Web Shared Domain Labels ──
export const APP_WEB_DOMAIN_LABELS: Record<number, string> = {
  [APP_WEB_SHARED_DOMAIN_ID.VideositeDotApp]: 'videosite.app',
  [APP_WEB_SHARED_DOMAIN_ID.CloudsiteDotApp]: 'cloudsite.app',
  [APP_WEB_SHARED_DOMAIN_ID.SiteCDNDotApp]: 'sitecdn.app',
};

// ── Spotlight Options ──
export const SPOTLIGHT_OPTIONS = {
  RANDOM_ANY: 'null', // null on API = random from any category
  DISABLED: 'none', // "none" string on API = disabled
} as const;

// ── Hero Style (app-level billboard treatment; design ADR-0016 D3, `app_cfg.hero_style`) ──
//
// Three ways the featured item's RAW poster is drawn on the TV home screen.
// The right one is a property of the ART (a lettered poster belongs in Card, a
// picture in Immersive; whether a picture may lose its top and bottom is the
// customer's call), so the customer chooses and the TV preview shows it.
export const HERO_STYLE = {
  CARD: 'card', // the poster whole, 960x540, crisp, beside the title (default)
  IMMERSIVE: 'immersive', // the poster fills the right of the band and melts into the background
  FULL_BLEED: 'full_bleed', // the poster fills the whole width, cropped top and bottom, title on the picture
} as const;

/** Every wire value, in the Select's order; anything else normalizes to card (fallback discipline). */
export const HERO_STYLES = [HERO_STYLE.CARD, HERO_STYLE.IMMERSIVE, HERO_STYLE.FULL_BLEED] as const;
export type HeroStyle = typeof HERO_STYLES[number];

export const HERO_STYLE_LABELS: Record<HeroStyle, string> = {
  [HERO_STYLE.CARD]: 'Card',
  [HERO_STYLE.IMMERSIVE]: 'Immersive',
  [HERO_STYLE.FULL_BLEED]: 'Full-bleed',
};

/** The General tab's Hero Style copy, one sentence pair per style (design ADR-0016 §Dashboard, verbatim). */
export const HERO_STYLE_DESCRIPTIONS: Record<HeroStyle, string> = {
  [HERO_STYLE.CARD]: 'Your poster is shown whole beside the title. Best for posters with text on them.',
  [HERO_STYLE.IMMERSIVE]:
    'Your poster fills the right of the screen and fades into the background. Best for pictures without text.',
  [HERO_STYLE.FULL_BLEED]:
    'Your poster fills the whole width; the top and bottom are cropped and the title sits on the picture. Check the preview.',
};

export const HERO_STYLE_OPTIONS = HERO_STYLES.map((value) => ({ value, label: HERO_STYLE_LABELS[value] }));

// ── Titles under artwork (app-level home-row caption policy; server-applied per contract) ──
export const TILE_TITLE_TREATMENT = {
  BELOW_CARD: 'below_card', // caption printed under the art (default)
  NONE: 'none', // art only — for artwork that carries its own title
} as const;

export const TILE_TITLE_OPTIONS = [
  { value: TILE_TITLE_TREATMENT.BELOW_CARD, label: 'Show titles under the artwork (recommended)' },
  { value: TILE_TITLE_TREATMENT.NONE, label: 'Art only (for artwork with the title in the image)' },
] as const;

/** The General tab's "Titles under artwork" help (plain words: no "tile" in customer-facing text). */
export const TILE_TITLE_HELP =
  'Whether the home screen shows each title under its artwork. Keep titles on when episodes share similar thumbnails. Choose art only when your artwork carries its own title, as Netflix and Apple TV do. Episode lists always show titles.';

// ── Constraints ──
export const APP_CONSTRAINTS = {
  NAME_MIN: 1,
  NAME_MAX: 150,
  DESC_MAX: 500,
  WEBSITE_MAX: 250,
  CUSTOM_NAME_MIN: 2,
  CUSTOM_NAME_MAX: 63,
  CUSTOM_DOMAIN_MAX: 253,
  FB_ID_MAX: 65,
  IG_ID_MAX: 40,
  X_ID_MAX: 25,
  YT_ID_MAX: 40,
  GA_ID_MAX: 20,
  ROKU_TITLE_MAX: 30, // Roku Channel Store channel-name limit
  ROKU_GENRES_MAX: 100,
  LISTING_URL_MAX: 500,
  LISTING_EMAIL_MAX: 254,
  LISTING_PHONE_MAX: 30,
  LISTING_AGE_RATING_MAX: 20,
} as const;

// ── Validation Patterns ──
export const APP_VALIDATION_PATTERNS = {
  FB_ID: /^[a-zA-Z0-9.]{1,65}$/,
  IG_ID: /^[a-zA-Z0-9._]{1,40}$/,
  X_ID: /^[a-zA-Z0-9_]{1,25}$/,
  YT_ID: /^(@[a-zA-Z0-9._-]{1,40}|UC[a-zA-Z0-9_-]{22})$/,
  GA_ID: /^(UA-\d{4,10}-\d{1,4}|G-[A-Z0-9]{4,15})$/i,
  CUSTOM_NAME: /^[a-z0-9]([a-z0-9]*(-[a-z0-9]+)*)?$/,
} as const;

// ── Query Keys ──
export const APP_QUERY_KEYS = {
  all: ['apps'] as const,
  list: (cId: number) => [...APP_QUERY_KEYS.all, 'list', cId] as const,
  detail: (cId: number, appCfgId: number) =>
    [...APP_QUERY_KEYS.all, 'detail', cId, appCfgId] as const,
  customNameCheck: (cId: number, name: string) =>
    [...APP_QUERY_KEYS.all, 'customNameCheck', cId, name] as const,
  legal: (cId: number, appCfgId: number) =>
    [...APP_QUERY_KEYS.all, 'legal', cId, appCfgId] as const,
} as const;
